import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { dataExchangeApi } from "@/services/data-exchange-api.service";
import type { DataExchangeReport, ImportRowErrorOut } from "@/types/api-v2";

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  parsing: "bg-blue-100 text-blue-800",
  validating: "bg-blue-100 text-blue-800",
  mapping: "bg-blue-100 text-blue-800",
  applying: "bg-blue-100 text-blue-800",
  committed: "bg-green-100 text-green-800",
  partial: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  "dry-run-ready": "bg-teal-100 text-teal-800",
  conflicts: "bg-amber-100 text-amber-800",
};

function fmt(t?: string | null): string {
  return t ? new Date(t).toLocaleString("ru-RU") : "—";
}

function ReportSummary({ report }: { report?: DataExchangeReport | null }) {
  if (!report) return <p className="text-sm text-muted-foreground">Отчёт не сформирован</p>;
  const s = report.summary;
  return (
    <div className="rounded-md border p-3 text-sm space-y-1">
      <div className="flex items-center gap-2">
        <span className="font-medium">Статус:</span>
        <Badge className={STATUS_BADGE[report.status] ?? ""}>{report.status}</Badge>
      </div>
      {s && (
        <p className="text-muted-foreground">
          Строк: {s.total ?? 0} · валидных: {s.valid ?? 0} · невалидных: {s.invalid ?? 0} · вопросов:{" "}
          {s.issues ?? 0}
        </p>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 pt-1 text-muted-foreground">
        <span>Создано объектов: {report.objects_created ?? 0}</span>
        <span>Обновлено объектов: {report.objects_updated ?? 0}</span>
        <span>Пропущено объектов: {report.objects_skipped ?? 0}</span>
        <span>Конфликтных: {report.objects_conflicts ?? 0}</span>
        <span>Заблокировано удалений: {report.objects_blocked_delete ?? 0}</span>
        <span>Медиа-файлов: {report.media_count ?? 0}</span>
        <span>Классификаторов создано: {report.classifiers_created ?? 0}</span>
        <span>Классификаторов обновлено: {report.classifiers_updated ?? 0}</span>
        <span>Классификаторов слито: {report.classifiers_merged ?? 0}</span>
      </div>
    </div>
  );
}

interface ImportBatchStatusDialogProps {
  batchId: number;
  onClose: () => void;
}

export default function ImportBatchStatusDialog({ batchId, onClose }: ImportBatchStatusDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [errorsLimit, setErrorsLimit] = useState(200);
  const [committing, setCommitting] = useState(false);

  const { data: batch, isLoading } = useQuery({
    queryKey: ["import-batch", batchId],
    queryFn: () => dataExchangeApi.getBatch(batchId),
    refetchInterval: (query) =>
      ["pending", "parsing", "validating", "mapping", "applying"].includes(
        query.state.data?.status ?? "",
      )
        ? 2000
        : false,
  });

  const { data: errors, isLoading: isLoadingErrors } = useQuery({
    queryKey: ["import-batch-errors", batchId, errorsLimit],
    queryFn: () => dataExchangeApi.getBatchErrors(batchId, 0, errorsLimit),
    enabled: !!batch && ["committed", "partial", "failed", "conflicts", "dry-run-ready"].includes(batch.status),
  });

  async function handleCommit() {
    if (!batch) return;
    setCommitting(true);
    try {
      await dataExchangeApi.commitBatch(batchId);
      toast({ title: "Импорт применён", description: "Батч закоммичен успешно." });
      queryClient.invalidateQueries({ queryKey: ["import-batch", batchId] });
      queryClient.invalidateQueries({ queryKey: ["import-batches"] });
    } catch (e: any) {
      toast({
        title: "Ошибка коммита",
        description: e?.message || "Не удалось применить импорт",
        variant: "destructive",
      });
    } finally {
      setCommitting(false);
    }
  }

  const report: DataExchangeReport | null | undefined = (batch?.dry_run_report ??
    batch?.result_report) as DataExchangeReport | null | undefined;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Импорт #{batchId}{batch?.archive_name ? ` — ${batch.archive_name}` : ""}</DialogTitle>
        </DialogHeader>

        {isLoading || !batch ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                <Badge variant="outline">mode: {batch.mode}</Badge>
                <Badge variant="outline">стратегия: {batch.strategy}</Badge>
                <Badge variant="outline">dry_run: {batch.dry_run ? "да" : "нет"}</Badge>
                <span>создан: {fmt(batch.created_at)}</span>
                <span>завершён: {fmt(batch.finished_at)}</span>
              </div>
              <ReportSummary report={report} />

              {errors !== undefined && errors.length > 0 && (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Лист</TableHead>
                        <TableHead>Строка</TableHead>
                        <TableHead>Код</TableHead>
                        <TableHead className="w-full">Сообщение</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoadingErrors ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-4">
                            <Loader2 className="inline animate-spin h-4 w-4" /> Загрузка ошибок…
                          </TableCell>
                        </TableRow>
                      ) : (
                        errors.map((e: ImportRowErrorOut) => (
                          <TableRow key={e.id}>
                            <TableCell className="whitespace-nowrap">{e.sheet_name}</TableCell>
                            <TableCell>{e.row_number}</TableCell>
                            <TableCell>
                              <Badge
                                className={
                                  e.error_code.startsWith("WARN_") || e.error_code.startsWith("IGNORED")
                                    ? "bg-amber-100 text-amber-800"
                                    : "bg-red-100 text-red-800"
                                }
                              >
                                {e.error_code}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-md whitespace-pre-wrap break-words align-top">
                              {e.message}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  {errors.length >= errorsLimit && (
                    <div className="p-2 text-center">
                      <Button variant="ghost" size="sm" onClick={() => setErrorsLimit((v) => v + 200)}>
                        Показать ещё
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {errors !== undefined && errors.length === 0 && (
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle2 className="h-4 w-4" /> Ошибок по строкам нет
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        <DialogFooter className="gap-2">
          {batch?.status === "dry-run-ready" && (
            <Button onClick={handleCommit} disabled={committing} className="gap-2">
              {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertCircle className="h-4 w-4" />}
              Применить импорт
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}